// Resolves where a mode switch should land so the user keeps their place, and
// defines the rail's Design/Run groupings for those same modes.
//
// Switching modes used to always drop you on the mode's landing page. When the
// current entity has an equivalent page in the target mode (a domain, product or
// process shown in both business and live), we navigate to that same entity
// instead. Dev mode is intentionally not preserved — switching to/from dev goes
// to the landing page.
import type { Component } from "vue";

import ActivityIcon from "@/assets/icons/activity.svg?component";
import HammerIcon from "@/assets/icons/hammer.svg?component";
import PenToolIcon from "@/assets/icons/pen-tool.svg?component";

export type Mode = "business" | "live" | "dev" | "build";

// Fixed landing route for the Overview row (see getModeGroups) — exported so
// callers checking "is this the Overview page" (e.g. activeSectionKey) can't
// drift from the route the row actually links to. Declared above LANDING,
// which reads it at module load.
export const BUSINESS_OVERVIEW_ROUTE_NAME = "businessOverview";

type RouteParams = Record<string, unknown>;

type StekBase = { orgName: string; stekName: string };

// Entity kinds that exist in more than one mode, keyed by the route param that
// identifies them. Only business/live have per-entity pages; dev and build are
// omitted, so they always fall back to their landing page.
const ENTITY_ROUTES: Array<{
    param: string;
    routes: Partial<Record<Mode, string>>;
}> = [
    {
        param: "domainId",
        routes: { business: "domainPage", live: "liveDomainPage" },
    },
    {
        param: "productId",
        routes: { business: "productPage", live: "liveProductPage" },
    },
    {
        param: "processId",
        routes: { business: "processPage", live: "liveProcessPage" },
    },
];

const LANDING: Record<Mode, string> = {
    business: BUSINESS_OVERVIEW_ROUTE_NAME,
    live: "livePage",
    dev: "devPage",
    build: "buildPage",
};

// Reverse lookup: a mapped route name → the entity definition it belongs to.
const ROUTE_TO_ENTITY = new Map<string, (typeof ENTITY_ROUTES)[number]>();
for (const entity of ENTITY_ROUTES) {
    for (const routeName of Object.values(entity.routes)) {
        if (routeName) ROUTE_TO_ENTITY.set(routeName, entity);
    }
}

export function resolveModeTarget(
    targetMode: Mode,
    currentRouteName: string | null | undefined,
    params: RouteParams,
    base: StekBase
): { name: string; params: RouteParams } {
    const landing = {
        name: LANDING[targetMode],
        params: { orgName: base.orgName, stekName: base.stekName },
    };

    const entity = currentRouteName
        ? ROUTE_TO_ENTITY.get(currentRouteName)
        : undefined;
    if (!entity) return landing;

    const targetName = entity.routes[targetMode];
    const id = params[entity.param];
    if (!targetName || id == null) return landing;

    return {
        name: targetName,
        params: {
            orgName: base.orgName,
            stekName: base.stekName,
            [entity.param]: id,
        },
    };
}

// Rail groups. Business, Strategy and IT Landscape are all "Design"; Build is
// its own group; Live is "Run".
export type ModeGroupId = "design" | "build" | "run";

export interface ModeGroupSection {
    // Stable row identity, independent of modeKey — Strategy and Business
    // both navigate within the "business" mode but must highlight/preview as
    // two distinct rows, so modeKey alone can't tell them apart.
    key: string;
    modeKey: Mode;
    label: string;
    icon: string;
    tag?: string;
    disabled: boolean;
    tooltip?: string;
    // A fixed landing route, bypassing resolveModeTarget's entity-preserving
    // logic — Strategy always lands on strategyVision, regardless of what
    // domain/product/process you were looking at.
    routeName?: string;
    // Overview has no worktree/tree of its own (see NavSideBar's
    // isOverviewRoute) — set on sections like it so NavRail skips the
    // hover-peek flyout entirely rather than opening one with nothing in it.
    noSubmenu?: boolean;
}

export interface ModeGroup {
    id: ModeGroupId;
    label: string;
    // Lucide icon (vite-svg-loader `?component` import) — used both in the
    // mode-switch track and the "Change mode" popup rows, so the two always
    // show the same glyph for a given mode.
    icon: Component;
    // CSS custom property (defined in _variables.scss) carrying the group's
    // accent color — no new colors are introduced here.
    accentVar: string;
    sections: ModeGroupSection[];
}

export interface ModeAvailability {
    devDisabled: boolean;
    liveDisabled: boolean;
    // Live is gated by subscription entitlement, not a stek setting, so the
    // reason varies (loading, unsubscribed, who can subscribe, a platform-wide
    // kill-switch) — the caller (useLiveModeTooltip) supplies the specific
    // message rather than this module guessing at one.
    liveDisabledTooltip?: string;
    // Build is unfinished — gated by a global env-based feature flag (see
    // OrganizationCapabilities.build), not a stek setting or entitlement.
    // Unlike dev/live, an unflagged Build has nothing to point the user at,
    // so it's omitted from the rail entirely rather than shown disabled.
    buildEnabled: boolean;
}

export function getModeGroups({
    devDisabled,
    liveDisabled,
    liveDisabledTooltip,
    buildEnabled,
}: ModeAvailability): ModeGroup[] {
    const groups: ModeGroup[] = [
        {
            id: "design",
            label: "Design",
            icon: PenToolIcon,
            accentVar: "--stekz-biz-color",
            sections: [
                {
                    key: "overview",
                    modeKey: "business",
                    label: "Overview",
                    icon: "pi pi-home",
                    disabled: false,
                    routeName: BUSINESS_OVERVIEW_ROUTE_NAME,
                    noSubmenu: true,
                },
                {
                    key: "strategy",
                    modeKey: "business",
                    label: "Strategy",
                    icon: "pi pi-compass",
                    disabled: false,
                    routeName: "strategyVision",
                },
                {
                    key: "business",
                    modeKey: "business",
                    label: "Business",
                    icon: "pi pi-eye",
                    disabled: false,
                    routeName: "stekPage",
                },
                {
                    key: "dev",
                    modeKey: "dev",
                    label: "IT Landscape",
                    icon: "pi pi-code",
                    tag: "ALPHA",
                    disabled: devDisabled,
                    tooltip: devDisabled
                        ? "Go to Stek Settings to enable Dev mode."
                        : undefined,
                },
            ],
        },
    ];

    if (buildEnabled) {
        groups.push({
            id: "build",
            label: "Build",
            icon: HammerIcon,
            accentVar: "--stekz-build-color",
            // One rail row per page, and none of them a tree — so, like
            // Overview, no panel and no flyout: the row IS the navigation.
            sections: BUILD_NAV_ITEMS.map((item) => ({
                key: item.key,
                modeKey: "build" as const,
                label: item.label,
                icon: item.icon,
                disabled: false,
                routeName: item.routeName,
                noSubmenu: true,
            })),
        });
    }

    groups.push({
        id: "run",
        label: "Run",
        icon: ActivityIcon,
        accentVar: "--stekz-ops-color",
        sections: [
            {
                key: "live",
                modeKey: "live",
                label: "Live",
                icon: "pi pi-play",
                tag: "BETA",
                disabled: liveDisabled,
                tooltip: liveDisabled ? liveDisabledTooltip : undefined,
            },
        ],
    });

    return groups;
}

// Which group a mode belongs to — used to highlight the active rail icon.
export function groupForMode(mode: Mode): ModeGroupId {
    if (mode === "live") return "run";
    if (mode === "build") return "build";
    return "design";
}

// Strategy pages all live within the "business" mode (same meta.mode as the
// domain/product/process tree), so telling them apart from the rest of
// Business needs a route-name check rather than just the mode.
const STRATEGY_ROUTE_NAMES = new Set([
    "strategyVision",
    "strategyObjectives",
    "strategyHorizons",
    "strategyWatchers",
    "strategyMarketCompetitors",
    "strategyMarketTrends",
]);

export function isStrategyRoute(routeName: string | null | undefined): boolean {
    return typeof routeName === "string" && STRATEGY_ROUTE_NAMES.has(routeName);
}

// The row key (see ModeGroupSection.key) that owns the panel/flyout slot for
// the current route — "strategy" and "overview" are the cases where that's
// not just the current mode.
export function activeSectionKey(
    mode: Mode,
    routeName: string | null | undefined
): string {
    if (mode === "business" && isStrategyRoute(routeName)) return "strategy";
    if (mode === "business" && routeName === BUSINESS_OVERVIEW_ROUTE_NAME)
        return "overview";
    if (mode === "build") return buildSectionKey(routeName);
    return mode;
}

// Build's three rail rows, in rail order. Each row lands on one route; the
// agent detail page counts as the Agents row.
export interface BuildNavItem {
    key: string;
    label: string;
    icon: string;
    routeName: string;
    routeNames: string[];
}
export const BUILD_NAV_ITEMS: BuildNavItem[] = [
    {
        key: "backlog",
        label: "Backlog",
        icon: "pi pi-list-check",
        routeName: "buildPage",
        routeNames: ["buildPage"],
    },
    {
        key: "compliance",
        label: "Compliance",
        icon: "pi pi-shield",
        routeName: "buildCompliance",
        routeNames: ["buildCompliance"],
    },
    {
        key: "agents",
        label: "Build agents",
        icon: "pi pi-microchip-ai",
        routeName: "buildAgents",
        routeNames: ["buildAgents", "buildAgentDetail", "buildConnection"],
    },
];

export function buildSectionKey(routeName: string | null | undefined): string {
    const item = BUILD_NAV_ITEMS.find((candidate) =>
        candidate.routeNames.includes(routeName ?? "")
    );
    return item?.key ?? "backlog";
}

// The Strategy nav's identity (route name, label, icon) — shared between
// NavSideBar's persisted list (which adds thread-diff badges on top) and
// NavRail's hover-preview flyout, so the two can never drift apart.
export interface StrategyNavItem {
    name: string;
    label: string;
    icon: string;
}
export const STRATEGY_NAV_ITEMS: StrategyNavItem[] = [
    {
        name: "strategyVision",
        label: "Vision & mission",
        icon: "pi pi-compass",
    },
    {
        name: "strategyObjectives",
        label: "Objectives",
        icon: "pi pi-objects-column",
    },
    { name: "strategyHorizons", label: "Horizons", icon: "pi pi-chart-line" },
    {
        name: "strategyMarketCompetitors",
        label: "Market",
        icon: "pi pi-chart-bar",
    },
];
export const STRATEGY_WATCHERS_ITEM: StrategyNavItem = {
    name: "strategyWatchers",
    label: "Watchers",
    icon: "pi pi-eye",
};
